import { useState } from "react";
import { createPortal } from "react-dom";
import { Layers, Check, Loader2, X, ShieldCheck, AlertTriangle } from "lucide-react";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();

export const FormulaChooser = ({ open, draft, formulas, submitting, existingLabels, onPick, onClose }) => {
  const [selected, setSelected] = useState(null);

  if (!open || !draft) return null;

  const list = formulas || [];
  const existingSet = new Set((existingLabels || []).map(norm));

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="formula-chooser">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !submitting && onClose()} />
      <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in-up">
        <button
          onClick={() => !submitting && onClose()}
          className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted transition"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <Layers className="w-5 h-5" />
        </div>
        <h2 className="font-heading text-xl font-bold tracking-tight mt-4">Ce document propose plusieurs formules</h2>
        <p className="text-sm text-muted-foreground mt-1">
          <span className="font-medium text-foreground">{draft.name}</span>{draft.insurer ? ` · ${draft.insurer}` : ""} — laquelle avez-vous souscrite ? On n'analyse que celle-là.
        </p>

        <div className="mt-5 space-y-3">
          {list.map((f, i) => {
            const isSel = selected === i;
            const dupes = (f.coverages || []).filter((g) => g.label && existingSet.has(norm(g.label)));
            return (
              <button
                key={i}
                data-testid={`formula-option-${i}`}
                onClick={() => setSelected(i)}
                disabled={submitting}
                className={`w-full text-left rounded-2xl border p-4 transition ${isSel ? "border-primary bg-accent/40 ring-1 ring-primary/30" : "border-border hover:border-primary/40"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`w-5 h-5 rounded-full grid place-items-center shrink-0 border ${isSel ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
                      {isSel && <Check className="w-3 h-3" />}
                    </span>
                    <p className="font-heading font-bold truncate">{f.label}</p>
                  </div>
                  {f.monthly_cost != null && (
                    <span className="text-sm font-mono font-semibold shrink-0">{f.monthly_cost} €/mois</span>
                  )}
                </div>
                {f.summary && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{f.summary}</p>}
                {(f.coverages || []).length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {f.coverages.slice(0, 5).map((g, k) => (
                      <span key={k} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        <ShieldCheck className="w-3 h-3 text-emerald-600" /> {g.label}
                      </span>
                    ))}
                    {f.coverages.length > 5 && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">+{f.coverages.length - 5}</span>
                    )}
                  </div>
                )}
                {dupes.length > 0 && (
                  <p data-testid={`formula-dupe-${i}`} className="mt-2.5 flex items-center gap-1.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Doublon possible : {dupes.length} garantie{dupes.length > 1 ? "s" : ""} déjà présente{dupes.length > 1 ? "s" : ""} ailleurs ({dupes.slice(0, 3).map((g) => g.label).join(", ")}{dupes.length > 3 ? "…" : ""})
                  </p>
                )}
              </button>
            );
          })}
        </div>

        <button
          data-testid="formula-confirm-btn"
          onClick={() => selected != null && onPick(list[selected])}
          disabled={submitting || selected == null}
          className="mt-5 w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-50 transition flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {selected == null ? "Sélectionnez votre formule" : "Valider cette formule"}
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default FormulaChooser;
